import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { TestimonialsProps } from "../types";

const SWIPE_THRESHOLD = 50;

const TestimonialSectionBlk = ({
  testimonials,
  title,
  isPaused,
}: TestimonialsProps) => {
  const [index, setIndex] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startX = useRef(0);
  const endX = useRef(0);
  const isDragging = useRef(false);

  const handleNext = useCallback(() => {
    setIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const handlePrev = useCallback(() => {
    setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const resetInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!isPaused) {
      intervalRef.current = setInterval(handleNext, 12000);
    }
  }, [handleNext, isPaused]);

  useEffect(() => {
    resetInterval();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetInterval]);

  const goToSlide = (slideIndex: number) => {
    setIndex(slideIndex);
    resetInterval();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    endX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = startX.current - endX.current;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      diff > 0 ? handleNext() : handlePrev();
      resetInterval();
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current) {
      endX.current = e.clientX;
    }
  };

  const handleMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    const diff = startX.current - endX.current;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      diff > 0 ? handleNext() : handlePrev();
      resetInterval();
    }
  };

  return (
    <div className="main-container-blk">
      <div className="mt-[72px] rounded-[40px] border-2 border-secondary-dark-navy-400 bg-secondary-dark-navy-500 px-24 py-14 flex flex-col justify-center gap-8 max-md:px-6 max-md:py-10">
        <h2 className="text-[#cdcfd3] text-center font-['Inter',Arial,Helvetica,sans-serif] text-lg font-normal">
          {title}
        </h2>

        <div
          className="relative"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.4 }}
            >
              <p className="flex flex-col justify-center overflow-hidden text-white text-center font-['Inter',Arial,Helvetica,sans-serif] text-2xl font-semibold tracking-[2px] max-md:text-base max-md:leading-normal">
                "{testimonials[index].quote}"
              </p>

              <div className="flex justify-center items-center mt-8 gap-4 max-md:w-[300px] max-md:mx-auto max-sm:justify-center">
                <div>
                  <img
                    src={testimonials[index].image}
                    alt="Client"
                    className="w-[76px]"
                  />
                </div>
                <div>
                  <h2 className="text-white font-['Inter',Arial,Helvetica,sans-serif] text-xl font-bold text-left max-[700px]:text-base">
                    {testimonials[index].author}
                  </h2>
                  <p className="text-[#cdcfd3] font-['Inter',Arial,Helvetica,sans-serif] text-base font-normal text-left max-[700px]:text-xs">
                    {testimonials[index].role}
                  </p>
                  {testimonials[index].company && (
                    <p className="text-[#cdcfd3] font-['Inter',Arial,Helvetica,sans-serif] text-base font-normal text-left italic max-[700px]:text-xs">
                      {testimonials[index].company}
                    </p>
                  )}
                  {testimonials[index].link && (
                    <a
                      href={testimonials[index].link}
                      rel="noopener noreferrer"
                      aria-label="View testimonial"
                    />
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex gap-2 mt-4 justify-center relative">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`size-2 rounded-full border-none cursor-pointer ${
                  i === index ? "bg-white" : "bg-[#353e4d]"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialSectionBlk;
